import "./kpiSparklineChart.scss";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { SparklineTypes } from "../types";
import { InsHighcharts } from "components/widgets/base";
import { InsLabelValueTooltip } from "components/widgets/base/common/insLabelValueTooltip";
import { useWidgetCurrentStartDate, useWidgetCurrentFromPeriod } from "components/widgets/hooks/use-current-widget";
import { FromPeriods } from "store/current-dashboard/types";
const threshold = 0.01;
const yAxisConfig = (min, max) => {
    const config = {
        endOnTick: false,
        labels: { enabled: false },
        startOnTick: false,
        tickPositions: [0],
        title: { text: null }
    };
    if (min === 0) {
        config.min = -threshold;
    }
    if (max === 0) {
        config.max = threshold;
    }
    return config;
};
const tooltipOptions = (enableTooltip) => ({
    enabled: enableTooltip,
    formatter() {
        if (this.point.options.custom?.tooltip) {
            return renderToStaticMarkup(React.createElement(InsLabelValueTooltip, { labelValuePairs: this.point.options.custom.tooltip }));
        }
        return "";
    },
    hideDelay: 0,
    outside: true,
    shared: false
});
const highchartsOptions = ({ allPoints, data, enableTooltip, filteredHistory, max, min, type, xAxisLabelOptions, xAxisTextPadding }) => ({
    chart: {
        className: type === SparklineTypes.colored && allPoints.some((x) => x.y !== 0) ? "sparkline-chart--colored" : "sparkline-chart--standard",
        styledMode: true
    },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: { line: { animation: false, marker: { enabled: false }, stickyTracking: false } },
    series: [
        { data: filteredHistory, type: "line" },
        { className: "ins-kpi-sparkline-dotted", data: data.future, type: "line" }
    ],
    title: { text: "" },
    tooltip: tooltipOptions(enableTooltip),
    xAxis: {
        labels: {
            formatter() {
                const tick = this.value === 0 ? null : new Date(this.value);
                return tick ? `${tick.toLocaleString("default", { month: "short" })} ${tick.getFullYear()}` : "";
            },
            y: xAxisTextPadding,
            ...xAxisLabelOptions
        },
        tickLength: 0,
        tickPositions: [
            filteredHistory?.[0]?.x || 0,
            filteredHistory?.[(filteredHistory?.length - 1) || 0]?.x || 0,
            data.future?.[(data.future?.length - 1) || 0]?.x || 0
        ],
        type: "datetime"
    },
    yAxis: yAxisConfig(min, max)
});
const getFilteredHistory = (data, startDate, fromPeriod) => {
    if (!data.history || !startDate || fromPeriod !== FromPeriods.Year) {
        return data.history;
    }
    const startTime = startDate.getTime();
    return data.history.filter((point) => {
        if (point.x === undefined) {
            return false;
        }
        return point.x !== null && point.x >= startTime;
    });
};
export const KpiSparklineChart = ({ data, type, sectionId, widgetId }) => {
    const startDate = useWidgetCurrentStartDate(sectionId, widgetId);
    const fromPeriod = useWidgetCurrentFromPeriod(sectionId, widgetId);
    const filteredHistory = getFilteredHistory(data, startDate, fromPeriod);
    const { min, max } = data.additionalData || {}, allPoints = [filteredHistory, data.future]
        .filter((d) => d?.length)
        .flatMap((d) => d), enableTooltip = allPoints.some((v) => v.custom?.tooltip), xAxisTextPadding = 12;
    const xAxisLabelOptions = data.future.length === 0 ? { reserveSpace: false } : { staggerLines: 2 };
    const options = highchartsOptions({
        allPoints,
        data,
        enableTooltip,
        filteredHistory,
        max,
        min,
        type,
        xAxisLabelOptions,
        xAxisTextPadding
    });
    if (type === SparklineTypes.colored && options.series) {
        options.series[0] = {
            ...options.series[0],
            negativeColor: "palceholder",
            threshold
        };
        if (options.series.length > 1) {
            options.series[1] = {
                ...options.series[1],
                negativeColor: "palceholder",
                threshold
            };
        }
    }
    return React.createElement(InsHighcharts, { key: `${min}-${max}`, options: options, sectionId: sectionId, widgetId: widgetId });
};
